import { Button } from "@shared-ui/button";
import type { ErrorInfo, ReactNode } from "react";
import React from "react";

interface ErrorBoundaryProps {
	children: ReactNode;
	/**
	 * Identifies this boundary in console logs (e.g. "AdminShell", "Approvals").
	 * Defaults to "Admin" for the root boundary.
	 */
	label?: string;
	/**
	 * Optional fallback. Receives the captured error and a `reset` callback the
	 * fallback can call to re-render the children.
	 */
	fallback?: (props: { error: Error; reset: () => void }) => ReactNode;
}

interface ErrorBoundaryState {
	error: Error | null;
}

export class ErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = { error: null };

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const label = this.props.label ?? "Admin";
		console.error(`[${label}] error boundary caught`, error, errorInfo);
	}

	private reset = () => this.setState({ error: null });

	private signOut = () => {
		// Hard reset: clear admin auth + persisted state and reload to login.
		try {
			window.localStorage.removeItem("finishd-admin-access-token");
			window.localStorage.removeItem("finishd-admin-refresh-token");
			window.localStorage.removeItem("finishd-admin-auth");
		} catch {
			// localStorage may be unavailable (private mode); falling through to
			// reload still recovers the UI.
		}
		window.location.href = "/login";
	};

	render() {
		const { error } = this.state;
		if (!error) return this.props.children;

		if (this.props.fallback) {
			return this.props.fallback({ error, reset: this.reset });
		}

		return (
			<div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
				<div className="grid w-full max-w-md gap-4 rounded-3xl border bg-white p-8 shadow-xl shadow-slate-200/60">
					<div className="grid gap-1">
						<h1 className="text-lg font-semibold text-slate-900">
							Something went wrong
						</h1>
						<p className="text-sm text-muted-foreground">
							The admin console hit an unexpected error. Try again, and if the
							problem persists, sign out to clear cached state.
						</p>
					</div>
					<pre className="max-h-40 overflow-auto rounded-2xl border bg-slate-50 p-3 font-mono text-xs text-slate-700">
						{error.message || String(error)}
					</pre>
					<div className="flex gap-2">
						<Button onClick={this.reset} className="flex-1">
							Try again
						</Button>
						<Button onClick={this.signOut} variant="outline" className="flex-1">
							Sign out
						</Button>
					</div>
				</div>
			</div>
		);
	}
}
